import { React, Link, PT } from '@gasbuddy/react';
import {
  Segment, Button, Checkbox, Message,
  Dimmer, Loader, Card, Divider,
} from 'semantic-ui-react';
import { Auth } from './Auth';
import { Role, RoleAction } from '../../roles';

function hasGroup(data, changes, group) {
  if (group in changes) {
    return changes[group];
  }
  if (data && data.groups) {
    return !!data.groups.find(g => g.name.join('.') === group);
  }
  return false;
}

export class AdminRoleList extends React.Component {
  static defaultProps = {
    loading: false,
    data: null,
  }

  static propTypes = {
    onSave: PT.func.isRequired,
    onResetPassword: PT.func.isRequired,
    onInvite: PT.func.isRequired,
    loading: PT.boolean,
    data: PT.shape({ groups: PT.arrayOf(PT.shape()) }),
  }

  state = { changes: {} };

  onSave(accountId) {
    const toAdd = [];
    const toRemove = [];
    const { changes } = this.state;
    const { onSave } = this.props;
    for (const [k, v] of Object.entries(changes)) {
      if (v) {
        toAdd.push(k);
      } else {
        toRemove.push(k);
      }
    }
    onSave(accountId, toAdd, toRemove);
  }

  getLink = (e) => {
    e.preventDefault();
    const { onInvite } = this.props;
    onInvite();
  }

  check = (group, isOn) => {
    const { changes } = this.state;
    this.setState({
      changes: {
        ...changes,
        [group]: isOn,
      },
    });
  }

  render() {
    const { loading, data, onResetPassword } = this.props;
    const { changes, reset } = this.state;
    const Rolebox = ({ group, ...rest }) => (
      <Checkbox
        onChange={(e, p) => this.check(group, p.checked)}
        defaultChecked={hasGroup(data, changes, group)}
        {...rest}
      />
    );
    const { hasPassword, has2FA } = data || {};

    return (
      <Dimmer.Dimmable dimmed={loading}>
        <Dimmer active={loading}>
          <Loader />
        </Dimmer>

        <Segment basic>
          {hasGroup(data, {}, 'admin.superuser')
            && <h3><i>SuperUser</i></h3>
          }

          <Auth forActions={RoleAction.CreateNewAdminUsers} hideUnauthorized>
            <Message hidden={hasPassword} warning>
              <Message.Header>This user does not yet exist</Message.Header>
              <p>
                Would you like to generate
                an&nbsp;
                <Link to="invitation" onClick={this.getLink}>invitation link</Link>
?
              </p>
            </Message>
            <Message hidden={has2FA || !hasPassword} warning>
              <Message.Header>This user does not have 2FA configured</Message.Header>
              <p>
                Would you like to generate an&nbsp;
                <Link to="invitation" onClick={this.getLink}>invitation link</Link>
?
              </p>
            </Message>
          </Auth>

          <Card.Group stackable>
            <Auth forActions={RoleAction.AdminAdminUsers} hideUnauthorized>
              <Card>
                <Card.Content>
                  <Card.Header>Identity</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="Manage Users" group={Role.Users} /></div>
                    <div><Rolebox label="Manage Groups" group={Role.Groups} /></div>
                    <div><Rolebox label="Admin Access" group={Role.AdminAdminAccess} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>

              <Card>
                <Card.Content>
                  <Card.Header>System</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="View System Logs" group={Role.SystemLogs} /></div>
                    <div><Rolebox label="View Features" group={Role.SystemFeaturesView} /></div>
                    <div><Rolebox label="Manage Features" group={Role.SystemFeaturesModify} /></div>
                    <div><Rolebox label="Manage Queues" group={Role.Queues} /></div>
                    <Divider />
                    <div><Rolebox label="Call identity-serv" group={`${Role.SystemApi}.identity-serv`} /></div>
                    <div><Rolebox label="Call loyalty-serv" group={`${Role.SystemApi}.loyalty-serv`} /></div>
                    <div><Rolebox label="Call membership-serv" group={`${Role.SystemApi}.membership-serv`} /></div>
                    <div><Rolebox label="Call user-content-serv" group={`${Role.SystemApi}.user-content-serv`} /></div>
                    <div><Rolebox label="Call vehicle-serv" group={`${Role.SystemApi}.vehicle-serv`} /></div>
                    <div><Rolebox label="Test Notifications" group={Role.TestNotifications} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>

              <Card>
                <Card.Content>
                  <Card.Header>Payments</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="View Payment Info" group={Role.PaymentView} /></div>
                    <div><Rolebox label="View Dwolla Report" group={Role.Dwolla} /></div>
                    <div><Rolebox label="Support Payment Customers" group={Role.PaymentSupport} /></div>
                    <div><Rolebox label="Credit Customers" group={Role.PaymentCredit} /></div>
                    <div><Rolebox label="Delete Customer Bank Accounts" group={Role.PaymentDeleteBanks} /></div>
                    <div><Rolebox label="Re-Bill Transactions" group={Role.PaymentRunBilling} /></div>
                    <div><Rolebox label="Reissue Wex Cards" group={Role.PaymentReissueWex} /></div>
                    <div><Rolebox label="Refund Transactions" group={Role.PaymentRefund} /></div>
                    <div><Rolebox label="Manage Coupons" group={Role.PaymentCoupons} /></div>
                    <div><Rolebox label="View Location Data" group={Role.ViewPaymentLocation} /></div>
                    <div><Rolebox label="Record Returned Cards" group={Role.RecordReturnedCards} /></div>
                    <div><Rolebox label="Create GasBack Codes" group={Role.CreateGasBackCodes} /></div>
                    <div><Rolebox label="Award Partner GasBack" group={Role.ManualGasBackSync} /></div>
                    <div><Rolebox label="Reconcile WEX transactions" group={Role.PaymentReconciliation} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>

            <Auth
              forActions={[RoleAction.AdminAdminUsers, RoleAction.AdminPaymentLevelUsers]}
              hideUnauthorized
            >
              <Card>
                <Card.Content>
                  <Card.Header>Payment Admin Levels</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="Level 1" group={Role.PaymentAdminLevel1} /></div>
                    <div><Rolebox label="Level 2" group={Role.PaymentAdminLevel2} /></div>
                    <div><Rolebox label="Level 3" group={Role.PaymentAdminLevel3} /></div>
                    <div><Rolebox label="Level 4" group={Role.PaymentAdminLevel4} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>
            <Auth forActions={[RoleAction.AdminAdminUsers]} hideUnauthorized>
              <Card>
                <Card.Content>
                  <Card.Header>POI Admin</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="POI Edtior" group={Role.POI} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>
            <Auth forActions={[RoleAction.AdminAdminUsers]} hideUnauthorized>
              <Card>
                <Card.Content>
                  <Card.Header>Business Pages</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="Admin" group={Role.BusinessPagesAdmin} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>
            <Auth forActions={[RoleAction.AdminAdminUsers]} hideUnauthorized>
              <Card>
                <Card.Content>
                  <Card.Header>GasBack Admin</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="Edit GasBack Campaigns" group={Role.GasBackCampaigns} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>
            <Auth forActions={[RoleAction.AdminAdminUsers]} hideUnauthorized>
              <Card>
                <Card.Content>
                  <Card.Header>Designer</Card.Header>
                  <Divider />
                  <Card.Description>
                    <div><Rolebox label="Author and publish Home screen layouts" group={Role.Designer} /></div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Auth>
          </Card.Group>

          {hasPassword && (
            <Auth forActions={RoleAction.AdminAdminUsers} hideUnauthorized>
              <Button
                loading={reset}
                style={{ position: 'fixed', top: 10, right: 10 }}
                primary
                onClick={() => this.setState({ reset: true }, () => onResetPassword(data.account_id))}
                icon="delete"
                labelPosition="left"
                content="Reset Password/2FA"
              />
            </Auth>
          )}

          <Button primary floated="right" onClick={() => this.onSave(data.account_id)}>Save Changes</Button>
        </Segment>
      </Dimmer.Dimmable>
    );
  }
}
